import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/api/client';
import { Input } from '@/components/atoms/Input';
import { Button } from '@/components/atoms/Button';
import { Badge } from '@/components/atoms/Badge';
import { Skeleton } from '@/components/atoms/Skeleton';
import { toast } from '@/store/toastStore';
import { useScrollLock } from '@/hooks/useScrollLock';

interface DisclosureItem {
  postId: number;
  title: string;
  createdAt: string;
  sourceUrl: string;
  rceptNo: string;
  analyzed: boolean;
  summaryPreview: string | null;
  telegramSent: boolean;
  telegramMessageId: number | null;
  telegramSentAt: string | null;
  telegramSentBy: string | null;
}

const LIMIT = 30;

function fmt(iso: string): string {
  return new Date(iso).toISOString().slice(0, 16).replace('T', ' ');
}

export function DisclosuresPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const analyzed = params.get('analyzed') ?? 'all';
  const sent = params.get('sent') ?? 'all';
  const page = Number(params.get('page') ?? 1);
  const [draftQ, setDraftQ] = useState(q);

  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'disclosures', { q, analyzed, sent, page }],
    queryFn: () => {
      const sp = new URLSearchParams();
      if (q) sp.set('q', q);
      if (analyzed !== 'all') sp.set('analyzed', analyzed);
      if (sent !== 'all') sp.set('sent', sent);
      sp.set('page', String(page));
      sp.set('limit', String(LIMIT));
      return get<{ items: DisclosureItem[]; page: number; limit: number; total: number }>(
        `/admin/disclosures?${sp}`,
      );
    },
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;

  const [confirmTarget, setConfirmTarget] = useState<DisclosureItem | null>(null);
  useScrollLock(confirmTarget !== null);

  const sendMutation = useMutation({
    mutationFn: (rceptNo: string) =>
      post<{ rceptNo: string; postId: number | null; messageId: number; sentAt: string }>(
        `/admin/disclosures/${rceptNo}/send-telegram`,
        {},
      ),
    onSuccess: (res) => {
      toast(`텔레그램 발송 완료 (msg_id=${res.messageId})`, 'success');
      setConfirmTarget(null);
      qc.invalidateQueries({ queryKey: ['admin', 'disclosures'] });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error?.message ?? err?.message ?? '발송 실패';
      toast(msg, 'error');
    },
  });

  function applyFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value && value !== 'all') next.set(key, value);
    else next.delete(key);
    next.set('page', '1');
    setParams(next);
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-extrabold md:text-2xl">공시 관리</h1>
        <p className="mt-1 text-sm text-[var(--color-text-sub)]">
          DART 공시 게시글 검색 · 분석 상태 확인 · 텔레그램 수동 발송
        </p>
      </div>

      {/* 검색 + 필터 */}
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <form
          className="flex flex-wrap gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            const next = new URLSearchParams(params);
            if (draftQ) next.set('q', draftQ);
            else next.delete('q');
            next.set('page', '1');
            setParams(next);
          }}
        >
          <Input
            value={draftQ}
            onChange={(e) => setDraftQ(e.target.value)}
            placeholder="종목명 또는 공시제목 검색"
            className="min-w-[280px] flex-1"
          />
          <select
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
            value={analyzed}
            onChange={(e) => applyFilter('analyzed', e.target.value)}
          >
            <option value="all">분석상태: 전체</option>
            <option value="true">분석 완료만</option>
            <option value="false">분석 대기만</option>
          </select>
          <select
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
            value={sent}
            onChange={(e) => applyFilter('sent', e.target.value)}
          >
            <option value="all">발송상태: 전체</option>
            <option value="true">발송 완료만</option>
            <option value="false">미발송만</option>
          </select>
          <Button type="submit">검색</Button>
        </form>
      </div>

      {/* 목록 */}
      <div className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : (data?.items.length ?? 0) === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--color-text-sub)]">조회된 공시가 없어요</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] text-sm">
              <thead className="bg-[var(--color-bg)] text-xs text-[var(--color-text-sub)]">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">공시</th>
                  <th className="px-3 py-3 text-left font-semibold">등록</th>
                  <th className="px-3 py-3 text-center font-semibold">분석</th>
                  <th className="px-3 py-3 text-center font-semibold">텔레그램</th>
                  <th className="px-3 py-3 text-right font-semibold">액션</th>
                </tr>
              </thead>
              <tbody>
                {data!.items.map((it) => (
                  <tr key={it.postId} className="border-t border-[var(--color-border)] hover:bg-[var(--color-bg)]/50">
                    <td className="max-w-[420px] px-4 py-3">
                      <a
                        href={`/post/${it.postId}`}
                        target="_blank"
                        rel="noopener"
                        className="block truncate font-medium hover:underline"
                      >
                        {it.title}
                      </a>
                      {it.summaryPreview && (
                        <div className="mt-1 truncate text-xs text-[var(--color-text-sub)]">
                          {it.summaryPreview}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-xs text-[var(--color-text-sub)] tabular-nums whitespace-nowrap">
                      {fmt(it.createdAt)}
                    </td>
                    <td className="px-3 py-3 text-center">
                      {it.analyzed ? (
                        <Badge variant="rise">완료</Badge>
                      ) : (
                        <Badge variant="fall">대기</Badge>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center">
                      {it.telegramSent ? (
                        <div>
                          <Badge variant="rise">발송 완료</Badge>
                          {it.telegramSentAt && (
                            <div className="mt-1 text-[11px] text-[var(--color-text-sub)] tabular-nums">
                              {fmt(it.telegramSentAt)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-[var(--color-text-sub)]">-</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <Button
                        size="sm"
                        variant={it.telegramSent ? 'ghost' : 'primary'}
                        disabled={it.telegramSent || !it.analyzed}
                        onClick={() => setConfirmTarget(it)}
                      >
                        {it.telegramSent ? '발송됨' : it.analyzed ? '텔레그램 발송' : '분석 대기 중'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 페이지네이션 */}
      {(data?.total ?? 0) > 0 && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <Button
            size="sm"
            variant="secondary"
            disabled={page <= 1}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('page', String(page - 1));
              setParams(next);
            }}
          >
            이전
          </Button>
          <span className="px-3 text-[var(--color-text-sub)]">
            {page} / {totalPages} (총 {data!.total}건)
          </span>
          <Button
            size="sm"
            variant="secondary"
            disabled={page >= totalPages}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('page', String(page + 1));
              setParams(next);
            }}
          >
            다음
          </Button>
        </div>
      )}

      {/* 발송 확인 modal */}
      {confirmTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-xl">
            <h3 className="text-lg font-bold">텔레그램 발송 확인</h3>
            <p className="mt-2 text-sm text-[var(--color-text-sub)]">
              아래 공시의 AI 분석을 텔레그램으로 발송합니다. 동일 공시는 중복 발송되지 않습니다.
            </p>
            <div className="mt-4 rounded-lg bg-[var(--color-bg)] p-3 text-sm">
              <div className="font-semibold">{confirmTarget.title}</div>
              <div className="mt-1 text-xs text-[var(--color-text-sub)] tabular-nums">
                rcept_no: {confirmTarget.rceptNo}
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirmTarget(null)} disabled={sendMutation.isPending}>
                취소
              </Button>
              <Button
                onClick={() => sendMutation.mutate(confirmTarget.rceptNo)}
                disabled={sendMutation.isPending}
              >
                {sendMutation.isPending ? '발송 중...' : '발송'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
